import { MenuItem, TextField } from "@mui/material";
import { useGetWebhookProvidersQuery } from "features/integrationsBeta/platformEnums";
import { useEffect } from "react";
import ConnectionBindingField from "./ConnectionBindingField";

interface Props {
  clientId: number;
  name: string;
  onNameChange: (name: string) => void;
  provider: string;
  onProviderChange: (provider: string) => void;
  connection: number | null;
  onConnectionChange: (id: number | null) => void;
}

/**
 * Webhook trigger fields shared by the Settings create dialog (immediate
 * POST) and the registration wizard (draft mode): optional label, provider +
 * the WEBHOOK connection of that provider's provider holding the inbound
 * Secret-Key.
 */
export default function WebhookTriggerFields({
  clientId,
  name,
  onNameChange,
  provider,
  onProviderChange,
  connection,
  onConnectionChange,
}: Props) {
  const { data: providers = [] } = useGetWebhookProvidersQuery();

  // Default to the first provider once the list loads (callback deliberately
  // not a dependency - parents recreate it per render).
  useEffect(() => {
    if (!provider && providers.length) {
      onProviderChange(providers[0].value);
    }
  }, [provider, providers]);

  return (
    <>
      <TextField
        size="small"
        label="Name (optional)"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        helperText="Label to tell sibling triggers apart, e.g. 'Paycom - new hires'."
      />

      <TextField
        select
        size="small"
        label="Provider"
        value={provider}
        onChange={(e) => {
          onProviderChange(e.target.value);
          onConnectionChange(null);
        }}
      >
        {providers.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>

      {provider && (
        <ConnectionBindingField
          clientId={clientId}
          requirement={{ connection_type: "WEBHOOK", provider_slug: provider }}
          value={connection}
          onChange={onConnectionChange}
          label="Secret-Key connection"
        />
      )}
    </>
  );
}
